import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

// Fires a short, warm-toned confetti burst — beige/terracotta/gold palette
// to stay consistent with the app's visual identity.
const fireCelebrationConfetti = () => {
  const palette = ['#C67D5B', '#A65D3F', '#D4A76A', '#FAE5D3', '#8FAE8F'];
  const baseOpts = {
    spread: 70,
    startVelocity: 35,
    gravity: 0.9,
    ticks: 90,
    colors: palette,
    scalar: 0.95,
    zIndex: 150,
  };
  // Burst 1: centre top
  confetti({ ...baseOpts, particleCount: 60, origin: { x: 0.5, y: 0.35 } });
  // Burst 2: left side, slight delay
  setTimeout(() => {
    confetti({ ...baseOpts, particleCount: 35, angle: 60, origin: { x: 0.15, y: 0.55 } });
  }, 120);
  // Burst 3: right side, slight delay
  setTimeout(() => {
    confetti({ ...baseOpts, particleCount: 35, angle: 120, origin: { x: 0.85, y: 0.55 } });
  }, 240);
};

// Lightweight, dependency-free celebration overlay shown after a successful
// Google Play / Stripe purchase. The caller controls visibility via `open`
// and provides a `onContinue` callback for the action button. The title and
// message are translated by the caller to keep this component i18n-agnostic.
//
// Visual style intentionally matches the app's beige/terracotta/sage palette
// and avoids any leftover overlay — clicking "Continuer" closes everything.
export default function PurchaseCelebrationModal({
  open,
  title,
  message,
  continueLabel,
  onContinue,
}) {
  // Tiny entrance animation: fade + lift after mount.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setShown(true), 30);
      // Fire confetti the moment the modal opens — runs on the body, above the modal.
      fireCelebrationConfetti();
      return () => clearTimeout(t);
    }
    setShown(false);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      data-testid="purchase-celebration-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop — clicking it does NOT close (we want explicit Continue) */}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />

      <div
        className={[
          'relative w-full max-w-sm rounded-3xl bg-velvet-paper',
          'border border-terracotta-main/30 shadow-2xl',
          'px-6 pt-10 pb-7 text-center',
          'transition-all duration-300 ease-out',
          shown ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95',
        ].join(' ')}
      >
        {/* Decorative sparkle ring */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2">
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-terracotta-main to-terracotta-dark flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2.2} />
            <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-300" />
            <Sparkles className="absolute -bottom-1 -left-2 w-4 h-4 text-amber-200" />
          </div>
        </div>

        <h2
          className="mt-6 text-2xl sm:text-3xl font-semibold text-velvet_text-primary leading-tight"
          style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
          data-testid="purchase-celebration-title"
        >
          {title}
        </h2>

        <p
          className="mt-4 text-base leading-relaxed text-velvet_text-primary/85"
          data-testid="purchase-celebration-message"
        >
          {message}
        </p>

        <Button
          onClick={onContinue}
          data-testid="purchase-celebration-continue-button"
          className="mt-7 w-full h-12 rounded-full bg-terracotta-main hover:bg-terracotta-dark text-white text-base font-medium shadow-md shadow-terracotta-main/30 transition-all"
        >
          {continueLabel}
        </Button>
      </div>
    </div>
  );
}
